import axios from 'axios'
import clsx from 'clsx'
import {FC, FormEvent, useEffect, useRef, useState} from 'react'
import {useAuth} from '../../../../auth'
import {ServiceFeeLine} from '../../../../business-services/components/ServiceFeeLine'
import {useTaxFileNames} from '../../../../extraService/inquiry/useTaxFileNames'
import {
  BuyerTaxFile,
  BuyerTaxFileFailure,
  BuyerTaxFileLookup,
  isLookupIdentifier,
  lookUpBuyerTaxFiles,
  nationalIdentifierLabel,
  normalizeLookupIdentifier,
  readBuyerTaxFileFailure,
} from './buyerTaxFile'
import './CustomerTaxFileLookup.css'

type Props = {
  /** The identifier typed before, when the user comes back to search again. */
  initialIdentifier?: string
  onChoose: (file: BuyerTaxFile) => void
  /** No file for the identifier: the buyer is entered with it all the same. */
  onWithoutFile: (identifier: string) => void
  /** The inquiry could not be made: the old way, type then the form. */
  onFallback: (identifier: string, failure: BuyerTaxFileFailure) => void
  /** No identifier, or a passport: the old way without an inquiry. */
  onManual: (identifier: string) => void
}

const INPUT_ID = 'mp-buyer-lookup-identifier'

/**
 * The first step of «افزودن خریدار» (2026-10-22): the buyer's national code, national id or economic code, the tax
 * files the organisation holds for it, and the one the user picks opens the form filled from it.
 */
const CustomerTaxFileLookup: FC<Props> = ({
  initialIdentifier = '',
  onChoose,
  onWithoutFile,
  onFallback,
  onManual,
}) => {
  const {currentUser} = useAuth()
  const companyId = Number(currentUser?.cpi || 0)
  const [identifier, setIdentifier] = useState(initialIdentifier)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [result, setResult] = useState<BuyerTaxFileLookup>()
  const controller = useRef<AbortController>()

  useEffect(() => () => controller.current?.abort(), [])

  const search = async (event?: FormEvent) => {
    event?.preventDefault()
    if (loading || !isLookupIdentifier(identifier)) return
    controller.current?.abort()
    const current = new AbortController()
    controller.current = current
    setLoading(true)
    setError('')
    setResult(undefined)
    try {
      setResult(await lookUpBuyerTaxFiles(identifier, current.signal))
    } catch (failure: unknown) {
      if (axios.isCancel(failure)) return
      const reason = readBuyerTaxFileFailure(failure)
      if (reason.correctable) setError(reason.message)
      else onFallback(normalizeLookupIdentifier(identifier), reason)
    } finally {
      if (controller.current === current) setLoading(false)
    }
  }

  const changeIdentifier = (value: string) => {
    setIdentifier(value)
    setError('')
    setResult(undefined)
  }

  const files = result?.items || []
  const names = useTaxFileNames(files)

  return (
    <section className='mp-buyer-lookup' aria-labelledby='mp-buyer-lookup-heading'>
      <h3 id='mp-buyer-lookup-heading' className='fs-5 fw-bold text-gray-800 mb-2'>
        استعلام پرونده مالیاتی خریدار
      </h3>
      <p className='text-gray-600 fs-7 mb-5'>
        با شناسه خریدار، پرونده‌های او از سامانه مؤدیان خوانده می‌شود؛ پرونده را انتخاب کنید تا نوع
        خریدار و شناسه‌هایش در فرم پر شود.
      </p>
      {companyId > 0 && (
        <ServiceFeeLine
          companyId={companyId}
          feature='buyerTaxFileInquiry'
          each='هر استعلام پرونده'
          className='mb-4'
        />
      )}

      <form className='mp-buyer-lookup__form' onSubmit={search} noValidate>
        <div className='mp-buyer-lookup__field'>
          <label htmlFor={INPUT_ID} className='form-label fw-bold required'>
            کد ملی، شناسه ملی یا شماره اقتصادی
          </label>
          <input
            id={INPUT_ID}
            className={clsx('form-control form-control-solid', error && 'is-invalid')}
            dir='ltr'
            inputMode='numeric'
            autoComplete='off'
            autoFocus
            maxLength={20}
            value={identifier}
            disabled={loading}
            aria-invalid={!!error}
            aria-describedby={`${INPUT_ID}-help${error ? ` ${INPUT_ID}-error` : ''}`}
            onChange={(event) => changeIdentifier(event.target.value)}
          />
          <small id={`${INPUT_ID}-help`} className='text-gray-600 fs-8'>
            کد ملی ۱۰ رقم، شناسه ملی ۱۱ رقم، کد فراگیر اتباع ۱۲ رقم یا شماره اقتصادی ۱۱ یا ۱۴ رقم؛
            بدون خط تیره.
          </small>
          {error && (
            <div id={`${INPUT_ID}-error`} className='invalid-feedback d-block' role='alert'>
              {error}
            </div>
          )}
        </div>
        <button
          type='submit'
          className='btn btn-primary mp-buyer-lookup__submit'
          disabled={loading || !isLookupIdentifier(identifier)}
        >
          {loading ? (
            <>
              <span className='spinner-border spinner-border-sm me-2' aria-hidden='true' />
              در حال استعلام…
            </>
          ) : (
            <>
              <i className='las la-search fs-3 me-1' aria-hidden='true' />
              استعلام پرونده
            </>
          )}
        </button>
      </form>

      <div aria-live='polite'>
        {loading && (
          <p className='text-gray-600 fs-7 mt-4 mb-0' role='status'>
            در حال خواندن پرونده‌ها از سامانه مؤدیان…
          </p>
        )}

        {result?.notFound && (
          <div className='mp-buyer-lookup__notice' role='status'>
            <i className='las la-info-circle fs-2 text-warning' aria-hidden='true' />
            <div>
              <p className='mb-3'>{result.message}</p>
              <button
                type='button'
                className='btn btn-sm btn-light-primary'
                onClick={() => onWithoutFile(result.identifier)}
              >
                ادامه با همین شناسه
                <i className='las la-angle-left ms-1' aria-hidden='true' />
              </button>
            </div>
          </div>
        )}

        {files.length > 0 && (
          <>
            <p className='fw-bold text-gray-800 mt-6 mb-3'>
              {files.length === 1
                ? 'یک پرونده پیدا شد؛ برای ادامه انتخابش کنید.'
                : `${files.length.toLocaleString(
                    'fa-IR'
                  )} پرونده پیدا شد؛ پرونده خریدار را انتخاب کنید.`}
            </p>
            <ul className='mp-buyer-lookup__files' aria-label='پرونده‌های پیدا شده'>
              {files.map((file) => (
                <li
                  key={`${file.economicCode || ''}-${file.nationalId || ''}`}
                  className='mp-buyer-lookup__file'
                >
                  <div className='mp-buyer-lookup__file-main'>
                    <strong className='mp-buyer-lookup__file-title'>
                      {file.brand ||
                        (file.economicCode && names[file.economicCode]) ||
                        (file.buyerTypeTitle ? `خریدار ${file.buyerTypeTitle}` : 'پرونده مالیاتی')}
                    </strong>
                    <dl className='mp-buyer-lookup__codes'>
                      {file.economicCode && (
                        <div>
                          <dt>شماره اقتصادی</dt>
                          <dd>
                            <bdi dir='ltr'>{file.economicCode}</bdi>
                          </dd>
                        </div>
                      )}
                      {file.nationalId && (
                        <div>
                          <dt>{nationalIdentifierLabel(file.buyerType)}</dt>
                          <dd>
                            <bdi dir='ltr'>{file.nationalId}</bdi>
                          </dd>
                        </div>
                      )}
                    </dl>
                    <div className='mp-buyer-lookup__chips'>
                      {file.buyerTypeTitle && (
                        <span className='badge badge-light-primary'>{file.buyerTypeTitle}</span>
                      )}
                      <span
                        className={clsx(
                          'badge',
                          file.active ? 'badge-light-success' : 'badge-light-warning'
                        )}
                      >
                        کارپوشه: {file.taxFileStatusTitle || 'نامشخص'}
                      </span>
                      {file.callStatusTitle && (
                        <span className='badge badge-light'>{file.callStatusTitle}</span>
                      )}
                      {file.existingCustomerId && (
                        <span className='badge badge-light-info'>در خریداران شما</span>
                      )}
                    </div>
                    {file.warning && <p className='mp-buyer-lookup__warning'>{file.warning}</p>}
                    {file.existingCustomerId && (
                      <p className='text-gray-600 fs-7 mb-0'>
                        {file.existingCustomerName ? `«${file.existingCustomerName}»` : 'خریداری'}{' '}
                        با همین شناسه در خریداران شما هست؛ ذخیره، اطلاعات همان خریدار را به‌روز
                        می‌کند.
                      </p>
                    )}
                  </div>
                  {file.selectable && (
                    <button
                      type='button'
                      className='btn btn-sm btn-primary mp-buyer-lookup__choose'
                      onClick={() => onChoose(file)}
                    >
                      انتخاب
                      <i className='las la-angle-left ms-1' aria-hidden='true' />
                    </button>
                  )}
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <div className='mp-buyer-lookup__manual'>
        <span className='text-gray-600 fs-7'>خریدار شناسه ندارد یا شماره گذرنامه دارد؟</span>
        <button
          type='button'
          className='btn btn-link btn-sm p-0 fs-7'
          disabled={loading}
          onClick={() => onManual(normalizeLookupIdentifier(identifier))}
        >
          ورود دستی بدون استعلام
        </button>
      </div>
    </section>
  )
}

export {CustomerTaxFileLookup}
